import { Timestamp } from 'firebase/firestore';
/**
 * History record for student data changes.
 * Stored in subcollection: students/{studentId}/history/{changeSetId}
 */
export interface StudentHistoryRecord {
  id?: string;
  timestamp: Timestamp;
  changedBy: string;
  changedByEmail: string;
  changeSource: string;
  changes: StudentFieldChange[];
  // Export tracking (backend-managed, read-only in frontend)
  exportProcessed?: boolean;
  exportProcessedOn?: Timestamp | null;
  exportError?: string | null;
}

export interface StudentFieldChange {
  field: string;
  oldValue: unknown;
  newValue: unknown;
}

/**
 * Mapping of technical field paths to German display labels.
 * Used in history timeline to show human-readable field names.
 * Inspired by export-defaults.json translations.
 */
export const STUDENT_HISTORY_FIELD_LABELS: Record<string, string> = {
  // Basic student info
  firstName: 'Vorname',
  lastName: 'Nachname',
  birthDate: 'Geburtsdatum',
  gender: 'Geschlecht',
  isActive: 'Aktiv',
  language: 'Sprache',
  schoolId: 'Schule',
  startingGrade: 'Startklasse',
  currentGrade: 'Aktuelle Klasse',
  classType: 'Klassentyp',
  currentSchoolYear: 'Schuljahr',
  holidayCare: 'Ferienbetreuung',
  'registration.userEmail': 'Anmelde-E-Mail / Telefon',
  'registration.status': 'Anmeldungsstatus',

  // Health info
  'healthInfo.allergies': 'Allergien vorhanden',
  'healthInfo.allergiesDescription': 'Allergien (Beschreibung)',
  'healthInfo.chronicDiseases': 'Chronische Erkrankungen vorhanden',
  'healthInfo.chronicDiseasesDescription': 'Chronische Erkrankungen (Beschreibung)',
  'healthInfo.measlesVaccination': 'Masernimpfung',
  'healthInfo.measlesVaccinationConsent': 'Masernimpfung (Einverständnis)',
  'healthInfo.tetanusVaccinationDate': 'Letzte Tetanusimpfung',
  'healthInfo.tetanusVaccinationNotPossible':
    'Tetanusimpfung nicht möglich (Kontraindikation)',
  'healthInfo.tetanusVaccinationDoctorCertificate':
    'Ärztliche Bescheinigung (Tetanus)',
  'healthInfo.nutrition': 'Ernährungsform',
  'healthInfo.nutritionOther': 'Ernährung (Sonstiges)',
  'healthInfo.emergencyMedication': 'Notfallmedikamente (Allergie)',

  // Parent info
  'parentInfo.singleParenting': 'Alleinerziehend',
  'parentInfo.hasSibling': 'Geschwisterkind',
  'parentInfo.siblingName': 'Name des Geschwisterkindes',
  'parentInfo.siblingUseCurrentSchool': 'Geschwisterkind an gleicher Schule',
  'parentInfo.siblingSchoolName': 'Schule des Geschwisterkindes',
  'parentInfo.parent1.firstName': 'Elternteil 1 – Vorname',
  'parentInfo.parent1.lastName': 'Elternteil 1 – Nachname',
  'parentInfo.parent1.email': 'Elternteil 1 – E-Mail',
  'parentInfo.parent1.phoneMobile': 'Elternteil 1 – Mobil',
  'parentInfo.parent1.phoneHome': 'Elternteil 1 – Festnetz',
  'parentInfo.parent1.phoneWork': 'Elternteil 1 – Arbeit',
  'parentInfo.parent1.employed': 'Elternteil 1 – Berufstätig',
  'parentInfo.parent1.occupation': 'Elternteil 1 – Beruf',
  'parentInfo.parent1.weeklyWorkHours': 'Elternteil 1 – Wochenarbeitsstunden',
  'parentInfo.parent1.legalGuardian': 'Elternteil 1 – Sorgeberechtigt',
  'parentInfo.parent1.address': 'Elternteil 1 – Adresse',
  'parentInfo.parent1.address.street': 'Elternteil 1 – Straße',
  'parentInfo.parent1.address.zipCode': 'Elternteil 1 – PLZ',
  'parentInfo.parent1.address.city': 'Elternteil 1 – Ort',
  'parentInfo.parent1.address.state': 'Elternteil 1 – Bundesland',
  'parentInfo.parent1.address.country': 'Elternteil 1 – Land',
  'parentInfo.parent1.address.addressManuallyEntered':
    'Elternteil 1 – Adresse manuell eingetragen',
  'parentInfo.parent2.firstName': 'Elternteil 2 – Vorname',
  'parentInfo.parent2.lastName': 'Elternteil 2 – Nachname',
  'parentInfo.parent2.email': 'Elternteil 2 – E-Mail',
  'parentInfo.parent2.phoneMobile': 'Elternteil 2 – Mobil',
  'parentInfo.parent2.phoneHome': 'Elternteil 2 – Festnetz',
  'parentInfo.parent2.phoneWork': 'Elternteil 2 – Arbeit',
  'parentInfo.parent2.employed': 'Elternteil 2 – Berufstätig',
  'parentInfo.parent2.occupation': 'Elternteil 2 – Beruf',
  'parentInfo.parent2.weeklyWorkHours': 'Elternteil 2 – Wochenarbeitsstunden',
  'parentInfo.parent2.legalGuardian': 'Elternteil 2 – Sorgeberechtigt',
  'parentInfo.parent2.address': 'Elternteil 2 – Adresse',
  'parentInfo.parent2.address.street': 'Elternteil 2 – Straße',
  'parentInfo.parent2.address.zipCode': 'Elternteil 2 – PLZ',
  'parentInfo.parent2.address.city': 'Elternteil 2 – Ort',
  'parentInfo.parent2.address.state': 'Elternteil 2 – Bundesland',
  'parentInfo.parent2.address.country': 'Elternteil 2 – Land',
  'parentInfo.parent2.address.addressManuallyEntered':
    'Elternteil 2 – Adresse manuell eingetragen',

  // Misc info
  'miscInfo.emergencyName': 'Notfallkontakt – Name',
  'miscInfo.emergencyPhone': 'Notfallkontakt – Telefon',
  'miscInfo.healthInsuranceName': 'Krankenkasse',
  'miscInfo.healthInsuranceWith': 'Versichert bei',
  'miscInfo.afterSchoolArrangement': 'Nachmittagsbetreuung',
  'miscInfo.afterSchoolArrangementDetails': 'Nachmittagsbetreuung (Details)',
  'miscInfo.photoConsentOptOut': 'Widerspruch Bildfreigabe',

  // Start info
  'startInfo.module': 'Betreuungsmodul',
  'startInfo.module.index': 'Betreuungsmodul – Nummer',
  'startInfo.module.description': 'Betreuungsmodul – Bezeichnung',
  'startInfo.module.fee.amount': 'Betreuungsmodul – Beitrag',
  'startInfo.module.fee.currency': 'Betreuungsmodul – Währung',
  'startInfo.module.fee.paymentFrequency': 'Betreuungsmodul – Zahlungsrhythmus',
  'startInfo.module.schedule.days': 'Betreuungsmodul – Tage',
  'startInfo.module.schedule.startTime': 'Betreuungsmodul – Startzeit',
  'startInfo.module.schedule.endTime': 'Betreuungsmodul – Endzeit',
  'startInfo.lunch': 'Mittagessen',
  'startInfo.lunch.description': 'Mittagessen – Bezeichnung',
  'startInfo.lunch.fee.amount': 'Mittagessen – Beitrag',
  'startInfo.lunch.fee.currency': 'Mittagessen – Währung',
  'startInfo.lunch.fee.paymentFrequency': 'Mittagessen – Zahlungsrhythmus',
  'startInfo.lunch.externalLunch': 'Mittagessen – extern',
  'startInfo.lunch.url': 'Mittagessen – Link (extern)',
  'startInfo.selectedLunchDays': 'Mittagessen – Tage',
  'startInfo.includeLunch': 'Mittagessen enthalten',
  'startInfo.school': 'Schule',
  'startInfo.school.schoolName': 'Schule – Name',
  'startInfo.school.address': 'Schule – Adresse',
  'startInfo.needsCareBeforeSchool': 'Betreuung vor Unterrichtsbeginn benötigt',
  careChangeEffectiveDate: 'Betreuungsdaten gültig ab',

  // Payment info (legacy)
  'paymentInfo.firstName': 'Zahlung – Vorname',
  'paymentInfo.lastName': 'Zahlung – Nachname',
  'paymentInfo.addressInfo': 'Zahlungsadresse',
  'paymentInfo.bankAccountInfo.iban': 'IBAN',
  'paymentInfo.bankAccountInfo.bic': 'BIC',
  'paymentInfo.bankAccountInfo.bankName': 'Bankname',
  'paymentInfo.bankAccountInfo.exported': 'Bankkonto exportiert',
  'paymentInfo.bankAccountInfo.exportedOn': 'Bankkonto exportiert am',
  'paymentInfo.consent': 'SEPA-Einverständnis',
  'paymentInfo.consentDate': 'SEPA-Einverständnis Datum',

  // Payment info (with references)
  'paymentInfo.paymentMethodId': 'Zahlungsmethode',
  'paymentInfo.mandateReference': 'Mandatsreferenz',
  'paymentInfo.mandateSignedOn': 'Mandat unterzeichnet am',
  'paymentInfo.maskedIban': 'IBAN (maskiert)',
  'paymentInfo.bic': 'BIC',
  'paymentInfo.bankName': 'Bankname',
  'paymentInfo.accountHolder': 'Kontoinhaber',
  'paymentInfo.mandateStatus': 'SEPA-Mandat Status',
  'paymentInfo.mandateRevokedOn': 'SEPA-Mandat widerrufen am',
  'paymentInfo.mandateRevokedNote': 'SEPA-Mandat Widerrufsnotiz',

  // Vertrag — nur über explizite Verlaufseinträge (#1185); der Change-Detector
  // überspringt `contract` komplett.
  'contract.status': 'Vertragsstatus',
  'contract.cancellationEffectiveDate': 'Kündigungsdatum',
  'contract.cancellationWithdrawalReason': 'Grund der Rücknahme',

  // Year info
  'yearInfo.startDate': 'Startdatum',
  'yearInfo.schoolYear': 'Schuljahr',
  'yearInfo.year': 'Schuljahr (Jahr)',
  'yearInfo.isCustomDate': 'Individuelles Startdatum',
};

/** Mapping of email type codes to German labels.
 * Template names follow the pattern: `${templateType}_${lang}` (e.g. contract_de)
 */
export const EMAIL_TYPE_LABELS: Record<string, string> = {
  // Template-based names (stored as ${type}_${lang} in emailSent)
  contract_de: 'Vertragsbestätigung',
  contract_en: 'Vertragsbestätigung (EN)',
  contractFromNew_de: 'Vertrag (Neuanmeldung)',
  contractFromNew_en: 'Vertrag (Neuanmeldung, EN)',
  contractFromWaitlist_de: 'Vertrag (Warteliste)',
  contractFromWaitlist_en: 'Vertrag (Warteliste, EN)',
  movedToWaitlist_de: 'Auf Warteliste gesetzt',
  movedToWaitlist_en: 'Auf Warteliste gesetzt (EN)',
  contractChangeConfirmation_de: 'Vertragsänderung',
  contractChangeConfirmation_en: 'Vertragsänderung (EN)',
  customStartDateConfirmed_de: 'Startdatum bestätigt',
  customStartDateConfirmed_en: 'Startdatum bestätigt (EN)',
  customStartDateChanged_de: 'Startdatum geändert',
  customStartDateChanged_en: 'Startdatum geändert (EN)',
  careInfoChange_de: 'Betreuungsänderung',
  careInfoChange_en: 'Betreuungsänderung (EN)',
  contractExtension_de: 'Vertragsverlängerung',
  contractExtension_en: 'Vertragsverlängerung (EN)',
  contractReminder_de: 'Vertragserinnerung',
  contractReminder_en: 'Vertragserinnerung (EN)',
  cancellationConfirmation_de: 'Kündigungsbestätigung',
  cancellationConfirmation_en: 'Kündigungsbestätigung (EN)',

  // Metadata type values (from sendEmail calls)
  alteration_cancellation_withdrawn: 'Kündigung zurückgenommen',
  care_info_change: 'Betreuungsänderung',
  careInfoChange: 'Betreuungsänderung',
  registration_status_update: 'Anmeldungsstatus',
  contract_status_update: 'Vertragsstatus',
  contract_reminder: 'Vertragserinnerung',
  contract_confirmation: 'Vertragsbestätigung',
  registration: 'Anmeldebestätigung',
  contact_form: 'Kontaktformular',

  // Customer-UI emails
  'password-reset': 'Passwort zurücksetzen',
  'email-verification': 'E-Mail-Verifizierung',
};

/**
 * Mapping of raw `registration.status` values to German labels for the history
 * timeline. Kept in sync with the registration_* labels in StudentStatusConfigs.
 */
export const REGISTRATION_STATUS_LABELS: Record<string, string> = {
  new: 'Neue Anmeldung',
  customStartDatePending: 'Startdatum wird geprüft',
  waitlist: 'Warteliste',
  contractFinalization: 'Vertragsphase',
  completed: 'Vertrag ausstehend',
  cancelled: 'Anmeldung gekündigt',
  deleted: 'Anmeldung gelöscht',
  cancellationRequested: 'Kündigung beantragt',
};

/**
 * Mapping of raw `contract.status` values to German labels for the history
 * timeline (#1185). Kept identical to `statusText()` in the employee UI
 * (`student-status.utils.ts`).
 */
export const CONTRACT_STATUS_LABELS: Record<string, string> = {
  active: 'Aktiver Vertrag',
  renewalPending: 'Verlängerung ausstehend',
  cancellationRequested: 'Kündigung beantragt',
  cancellationApproved: 'Kündigung genehmigt',
  cancelled: 'Vertrag gekündigt',
  terminated: 'Vertrag beendet',
};

/** Mapping of change source identifiers to readable labels */
export const CHANGE_SOURCE_LABELS: Record<string, string> = {
  'employee-ui': 'Mitarbeiter App',
  'customer-ui': 'AWO Care Portal',
  customer_ui: 'AWO Care Portal',
  function: 'System',
  system: 'System',
  payment_method_update: 'Zahlungsdaten-Aktualisierung',
  phoneToEmailConversion: 'Umstellung auf E-Mail',
};
